<template>
  <b-modal
    id="modal-reference-labels"
    title="Imprimer les étiquettes QR code"
    size="lg"
    ok-title="Imprimer"
    cancel-title="Annuler"
    :ok-disabled="inGenerating || totalLabels === 0"
    @ok.prevent="print()"
    @show="reset()"
  >
    <!-- QR-005 : le périmètre. La sélection ne peut porter que sur la page affichée
         (la liste est paginée côté serveur) : on propose donc explicitement d'étendre
         l'impression à tout le résultat du filtre, sans avoir à parcourir les pages. -->
    <b-form-group v-if="allowFilterScope" label="Que faut-il imprimer ?">
      <b-form-radio v-model="scope" value="selection" :disabled="!selectedRows.length">
        Les {{ selectedRows.length }} référence(s) sélectionnée(s)
        <span v-if="!selectedRows.length" class="text-muted">— aucune ligne cochée</span>
      </b-form-radio>
      <b-form-radio v-model="scope" value="filters">
        Les {{ filteredTotal }} référence(s) correspondant à la recherche en cours
      </b-form-radio>
    </b-form-group>

    <template v-if="scope === 'selection'">
      <b-form-group label="Nombre d'étiquettes par référence">
        <b-input-group style="max-width: 320px">
          <b-form-input type="number" min="1" :max="maxQuantity" v-model.number="bulkQuantity" />
          <b-input-group-append>
            <b-button variant="outline-secondary" @click="applyToAll()">Appliquer à toutes</b-button>
          </b-input-group-append>
        </b-input-group>
      </b-form-group>

      <div class="table-wrapper">
        <b-table :items="rows" :fields="fields" small>
          <template slot="quantity" slot-scope="data">
            <b-form-input
              type="number"
              min="1"
              :max="maxQuantity"
              v-model.number="data.item.quantity"
              style="max-width: 90px"
            />
          </template>
        </b-table>
      </div>
    </template>

    <b-form-group v-else label="Nombre d'étiquettes par référence">
      <b-form-input
        type="number"
        min="1"
        :max="maxQuantity"
        v-model.number="bulkQuantity"
        style="max-width: 120px"
      />
    </b-form-group>

    <!-- Garde-fou : le total est affiché avant lancement. Une sélection large multipliée
         par 3 atteint vite des volumes qu'on ne veut pas envoyer par erreur à une
         imprimante d'entrepôt. -->
    <b-alert :variant="totalLabels > maxLabels ? 'danger' : 'info'" show>
      <strong>{{ totalLabels }}</strong> étiquette(s) seront générées.
      <span v-if="totalLabels > maxLabels">
        Au-delà de {{ maxLabels }}, l'impression est refusée : affinez la sélection.
      </span>
    </b-alert>

    <div v-if="inGenerating" class="text-center">
      <div class="loader"></div>
      Génération du PDF en cours...
    </div>
  </b-modal>
</template>

<script>
export default {
  name: "ReferenceLabelsModal",
  props: {
    selectedRows: {
      type: Array,
      default: () => [],
    },
    // Critères de la liste, transmis tels quels au back pour le mode "tout le filtre".
    filters: {
      type: Object,
      default: () => ({}),
    },
    filteredTotal: {
      type: Number,
      default: 0,
    },
    // La fiche référence n'imprime qu'une seule étiquette : il n'y a ni sélection
    // multiple ni filtre de liste à étendre, le choix de périmètre est masqué.
    allowFilterScope: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      // Une étiquette sur la face, une sur la tranche, une sur la pochette de documents.
      defaultQuantity: 3,
      maxQuantity: 50,
      maxLabels: 2000,
      scope: "selection",
      bulkQuantity: 3,
      rows: [],
      inGenerating: false,
      fields: [
        { key: "internal_code", label: "Code interne" },
        { key: "product", label: "Produit" },
        { key: "quantity", label: "Étiquettes" },
      ],
    };
  },
  computed: {
    totalLabels() {
      if (this.scope === "filters") {
        return this.filteredTotal * this.sanitize(this.bulkQuantity);
      }
      return this.rows.reduce((total, row) => total + this.sanitize(row.quantity), 0);
    },
  },
  watch: {
    selectedRows() {
      this.buildRows();
    },
  },
  methods: {
    reset() {
      this.bulkQuantity = this.defaultQuantity;
      this.scope =
        this.selectedRows.length || !this.allowFilterScope ? "selection" : "filters";
      this.buildRows();
    },
    buildRows() {
      this.rows = this.selectedRows.map((row) => ({
        id: row.id,
        internal_code: row.internal_code,
        product: row.product,
        quantity: this.defaultQuantity,
      }));
    },
    applyToAll() {
      const quantity = this.sanitize(this.bulkQuantity);
      this.rows.forEach((row) => {
        row.quantity = quantity;
      });
    },
    sanitize(quantity) {
      const value = parseInt(quantity, 10);
      if (isNaN(value) || value < 1) {
        return 1;
      }
      return Math.min(value, this.maxQuantity);
    },
    print() {
      if (this.totalLabels > this.maxLabels) {
        this.$toastr.error(
          "Impression limitée à " + this.maxLabels + " étiquettes par lot. Affinez la sélection."
        );
        return;
      }

      const payload =
        this.scope === "filters"
          ? { filters: this.filters, quantity: this.sanitize(this.bulkQuantity) }
          : {
              references: this.rows.map((row) => ({
                id: row.id,
                quantity: this.sanitize(row.quantity),
              })),
            };

      this.inGenerating = true;
      this.$store
        .dispatch("reference/printLabels", payload)
        .then((blob) => {
          this.download(blob);
          this.$root.$emit("bv::hide::modal", "modal-reference-labels");
        })
        .catch(() => {
          this.$toastr.error("La génération des étiquettes a échoué");
        })
        .then(() => {
          this.inGenerating = false;
        });
    },
    download(blob) {
      const url = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "etiquettes-qr.pdf");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/Reference/ReferenceLabelsModal.vue